import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useAlert } from "../../contexts/AlertContext";
import { transactionAPI, walletAPI } from "../../services/api";
import { Transaction } from "../../types";
import {
  MdAccountBalance,
  MdTrendingUp,
  MdCardGiftcard,
  MdRefresh,
  MdArrowUpward,
  MdArrowDownward,
  MdPayment,
  MdHistory,
  MdSecurity,
  MdVerifiedUser,
  MdEmail,
  MdAccountBalanceWallet,
  MdSavings,
  MdSwapHoriz,
  MdRemove,
  MdWallet as MdCrypto,
} from "react-icons/md";
import GlobalStyles, { CommonClasses } from "../../global/GlobalStyle";
import WalletConnectionModal from "../../components/wallet/WalletConnectionModal";

const DashboardOverview = () => {
  const { user, refreshUser } = useAuth();
  const { addAlert } = useAlert();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    totalDeposits: 0,
    totalWithdrawals: 0,
    pendingTransactions: 0,
  });
  const [showWalletModal, setShowWalletModal] = useState(false);
  const [userWallets, setUserWallets] = useState<any[]>([]);
  const [isLoadingWallets, setIsLoadingWallets] = useState(true);
  const [cryptoPrices, setCryptoPrices] = useState<any>({});

  const BalanceIcon = MdAccountBalance as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const TrendIcon = MdTrendingUp as React.FC<React.SVGProps<SVGSVGElement>>;
  const GiftIcon = MdCardGiftcard as React.FC<React.SVGProps<SVGSVGElement>>;
  const RefreshIcon = MdRefresh as React.FC<React.SVGProps<SVGSVGElement>>;
  const ArrowUpIcon = MdArrowUpward as React.FC<React.SVGProps<SVGSVGElement>>;
  const ArrowDownIcon = MdArrowDownward as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const PaymentIcon = MdPayment as React.FC<React.SVGProps<SVGSVGElement>>;
  const HistoryIcon = MdHistory as React.FC<React.SVGProps<SVGSVGElement>>;
  const SecurityIcon = MdSecurity as React.FC<React.SVGProps<SVGSVGElement>>;
  const VerifiedIcon = MdVerifiedUser as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const EmailIcon = MdEmail as React.FC<React.SVGProps<SVGSVGElement>>;
  const WalletIcon = MdAccountBalanceWallet as React.FC<
    React.SVGProps<SVGSVGElement>
  >;
  const SavingsIcon = MdSavings as React.FC<React.SVGProps<SVGSVGElement>>;
  const TransferIcon = MdSwapHoriz as React.FC<React.SVGProps<SVGSVGElement>>;
  const RemoveIcon = MdRemove as React.FC<React.SVGProps<SVGSVGElement>>;
  const CryptoIcon = MdCrypto as React.FC<React.SVGProps<SVGSVGElement>>;

  // Fetch crypto prices
  const fetchCryptoPrices = async () => {
    try {
      const response = await transactionAPI.getCryptoPrices();
      setCryptoPrices(response.data);
    } catch (error) {
      console.error("Failed to fetch crypto prices:", error);
      // Set fallback prices
      setCryptoPrices({
        bitcoin: { usd: 45000 },
        ethereum: { usd: 2500 },
        tether: { usd: 1 },
        solana: { usd: 100 },
        ripple: { usd: 0.6 },
        binancecoin: { usd: 350 },
        stellar: { usd: 0.12 },
        zora: { usd: 0.5 },
        dogecoin: { usd: 0.08 },
      });
    }
  };

  // Fetch user wallets separately
  const fetchUserWallets = async () => {
    try {
      setIsLoadingWallets(true);
      const response = await walletAPI.getUserWallets();
      setUserWallets(response.data || []);
    } catch (error: any) {
      console.error("Failed to fetch user wallets:", error);
      // Fallback to user.wallets if available
      setUserWallets(user?.wallets || []);
    } finally {
      setIsLoadingWallets(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      const [transactionsResponse] = await Promise.all([
        transactionAPI.getTransactions(),
        fetchUserWallets(), // Fetch wallets along with transactions
      ]);

      const transactions = transactionsResponse.data;
      const recent = transactions.slice(0, 5);
      setRecentTransactions(recent);

      const totalDeposits = transactions
        .filter((t) => t.type === "deposit" && t.status === "confirmed")
        .reduce((sum, t) => sum + Number(t.amount), 0);

      const totalWithdrawals = transactions
        .filter(
          (t) =>
            (t.type === "withdrawal" || t.type === "charge") &&
            t.status === "confirmed",
        )
        .reduce((sum, t) => sum + Number(t.amount), 0);

      const pendingTransactions = transactions.filter(
        (t) => t.status === "pending",
      ).length;

      setStats({
        totalDeposits,
        totalWithdrawals,
        pendingTransactions,
      });
    } catch (error: any) {
      console.error("Failed to fetch dashboard data:", error);
      addAlert({ type: "error", message: "Failed to load dashboard data" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshUser();
      await fetchDashboardData();
      await fetchCryptoPrices();
      addAlert({ type: "success", message: "All data updated!" });
    } catch (error) {
      addAlert({ type: "error", message: "Failed to refresh data" });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleWalletConnect = async (walletData: any) => {
    await refreshUser();
    await fetchUserWallets(); // Refresh wallets after connecting
    setShowWalletModal(false);
    addAlert({ type: "success", message: "Wallet connected successfully!" });
  };

  useEffect(() => {
    fetchDashboardData();
    fetchCryptoPrices();
    // eslint-disable-next-line
  }, []);

  // Load wallets when user changes
  useEffect(() => {
    if (user) {
      // Use wallets from user object initially
      setUserWallets(user.wallets || []);
      fetchUserWallets();
    }
    // eslint-disable-next-line
  }, [user]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "confirmed":
        return <VerifiedIcon className="w-4 h-4 text-green-500" />;
      case "pending":
        return <HistoryIcon className="w-4 h-4 text-yellow-500" />;
      case "failed":
      case "rejected":
        return <RemoveIcon className="w-4 h-4 text-red-500" />;
      default:
        return <HistoryIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
      case "failed":
      case "rejected":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "deposit":
        return <ArrowDownIcon className="w-4 h-4 text-green-500" />;
      case "withdrawal":
        return <ArrowUpIcon className="w-4 h-4 text-red-500" />;
      case "charge":
        return <RemoveIcon className="w-4 h-4 text-red-500" />;
      case "bonus":
        return <GiftIcon className="w-4 h-4 text-purple-500" />;
      case "transfer":
        return <TransferIcon className="w-4 h-4 text-blue-500" />;
      default:
        return <PaymentIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "deposit":
        return "text-green-600 dark:text-green-400";
      case "withdrawal":
      case "charge":
        return "text-red-600 dark:text-red-400";
      case "bonus":
        return "text-purple-600 dark:text-purple-400";
      case "transfer":
        return "text-blue-600 dark:text-blue-400";
      default:
        return "text-gray-600 dark:text-gray-400";
    }
  };

  const getBalanceTypeIcon = (balanceType: string) => {
    switch (balanceType) {
      case "ledger":
        return <BalanceIcon className="w-4 h-4 text-blue-500" />;
      case "stake":
        return <TrendIcon className="w-4 h-4 text-green-500" />;
      case "bonus":
        return <GiftIcon className="w-4 h-4 text-purple-500" />;
      case "earnings":
        return <SavingsIcon className="w-4 h-4 text-orange-500" />;
      default:
        return <BalanceIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  const getBalanceTypeColor = (balanceType: string) => {
    switch (balanceType) {
      case "ledger":
        return "text-blue-600 dark:text-blue-400";
      case "stake":
        return "text-green-600 dark:text-green-400";
      case "bonus":
        return "text-purple-600 dark:text-purple-400";
      case "earnings":
        return "text-orange-600 dark:text-orange-400";
      default:
        return "text-gray-600 dark:text-gray-400";
    }
  };

  const formatAmount = (transaction: Transaction) => {
    const amount = Number(transaction.amount);
    const formattedAmount = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    }).format(amount);

    if (transaction.type === "charge" || transaction.type === "withdrawal") {
      return `-${formattedAmount}`;
    }
    return formattedAmount;
  };

  const getAmountColor = (transaction: Transaction) => {
    if (transaction.type === "charge" || transaction.type === "withdrawal") {
      return "text-red-600 dark:text-red-400";
    }
    return "text-green-600 dark:text-green-400";
  };

  const balanceCards = [
    {
      title: "Ledger Balance",
      amount: Number(user?.ledger_balance) || 0,
      icon: BalanceIcon,
      gradient: "from-blue-500 to-blue-600",
    },
    {
      title: "Stake Balance",
      amount: Number(user?.stake_balance) || 0,
      icon: TrendIcon,
      gradient: "from-green-500 to-green-600",
    },
    {
      title: "Bonus Balance",
      amount: Number(user?.bonus_balance) || 0,
      icon: GiftIcon,
      gradient: "from-purple-500 to-purple-600",
    },
    {
      title: "Earnings Balance",
      amount: Number(user?.earnings_balance) || 0,
      icon: SavingsIcon,
      gradient: "from-orange-500 to-orange-600",
    },
    {
      title: "Total Balance",
      amount:
        (Number(user?.ledger_balance) || 0) +
        (Number(user?.stake_balance) || 0) +
        (Number(user?.bonus_balance) || 0) +
        (Number(user?.earnings_balance) || 0),
      icon: BalanceIcon,
      gradient: "from-purple-600 to-pink-600",
      fullWidth: true,
    },
  ];

  const statCards = [
    {
      title: "Total Deposits",
      value: `$${stats.totalDeposits.toFixed(2)}`,
      icon: ArrowDownIcon,
      color: "text-green-500",
      bgColor: "bg-green-50 dark:bg-green-900/20",
    },
    {
      title: "Total Withdrawals",
      value: `$${stats.totalWithdrawals.toFixed(2)}`,
      icon: ArrowUpIcon,
      color: "text-red-500",
      bgColor: "bg-red-50 dark:bg-red-900/20",
    },
    {
      title: "Pending Transactions",
      value: stats.pendingTransactions.toString(),
      icon: HistoryIcon,
      color: "text-yellow-500",
      bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
    },
  ];

  const quickActions = [
    {
      title: "Deposit Funds",
      description: "Add funds to your account",
      icon: ArrowDownIcon,
      link: "/dashboard/deposit",
      color:
        "bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700",
    },
    {
      title: "Withdraw Funds",
      description: "Withdraw to external wallet",
      icon: ArrowUpIcon,
      link: "/dashboard/withdraw",
      color:
        "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700",
    },
    {
      title: "View Transactions",
      description: "Check your transaction history",
      icon: HistoryIcon,
      link: "/dashboard/transactions",
      color:
        "bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700",
    },
    {
      title: "Verify Account",
      description: "Complete KYC verification",
      icon: SecurityIcon,
      link: "/dashboard/kyc",
      color:
        "bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700",
    },
  ];

  const formatBalanceAmount = (amount: number) => {
    return typeof amount === "number"
      ? amount.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "0.00";
  };

  // Calculate connected wallets
  const connectedWallets = userWallets.filter((wallet) => wallet.is_connected);
  const hasConnectedWallets = connectedWallets.length > 0;

  // Helper to safely access crypto balances
  const getCryptoBalance = (coin: string): number => {
    if (!user) return 0;

    const coinKey = `${coin.toLowerCase()}_balance` as
      | "btc_balance"
      | "eth_balance"
      | "usdt_balance"
      | "sol_balance"
      | "xrp_balance"
      | "bnb_balance"
      | "xlm_balance"
      | "zora_balance"
      | "doge_balance";

    return Number(user[coinKey]) || 0;
  };

  // Calculate USD value for each crypto
  const getCryptoUSDValue = (coinSymbol: string, balance: number): number => {
    const coinMap: { [key: string]: string } = {
      BTC: "bitcoin",
      ETH: "ethereum",
      USDT: "tether",
      SOL: "solana",
      XRP: "ripple",
      BNB: "binancecoin",
      XLM: "stellar",
      ZORA: "zora",
      DOGE: "dogecoin",
    };

    const coinId = coinMap[coinSymbol];
    if (coinId && cryptoPrices[coinId]?.usd) {
      return balance * cryptoPrices[coinId].usd;
    }
    return 0;
  };

  // Crypto coins to display
  const cryptoCoins = [
    { symbol: "BTC", name: "Bitcoin" },
    { symbol: "ETH", name: "Ethereum" },
    { symbol: "USDT", name: "Tether" },
    { symbol: "SOL", name: "Solana" },
    { symbol: "XRP", name: "Ripple" },
    { symbol: "BNB", name: "Binance Coin" },
    { symbol: "XLM", name: "Stellar" },
    { symbol: "ZORA", name: "Zora" },
    { symbol: "DOGE", name: "Dogecoin" },
  ];

  // Calculate total crypto portfolio value
  const totalCryptoValue = cryptoCoins.reduce((total, coin) => {
    const balance = getCryptoBalance(coin.symbol);
    const usdValue = getCryptoUSDValue(coin.symbol, balance);
    return total + usdValue;
  }, 0);

  return (
    <div className={GlobalStyles.container}>
      {/* Header */}
      <div
        className={`${CommonClasses.flexBetween} max-sm:flex-col max-sm:items-start max-sm:gap-4`}
      >
        <div>
          <h1
            className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong}`}
          >
            Welcome back, {user?.first_name}! 👋
          </h1>
          <p className={`${CommonClasses.textGray} mt-2`}>
            Here's your financial overview and recent activity.
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing}
          className={`${GlobalStyles.buttonIconOutline} ${
            isRefreshing ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <RefreshIcon
            className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
          />
        </button>
      </div>

      {/* Email Verification Banner */}
      {!user?.email_verified_at && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-2xl p-6 mb-6">
          <div
            className={`${CommonClasses.flexBetween} max-sm:flex-col max-sm:gap-2`}
          >
            <div
              className={`${CommonClasses.flexStart} gap-3 max-sm:flex-col max-sm:gap-2`}
            >
              <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center">
                <EmailIcon className="w-5 h-5 text-white" />
              </div>
              <div className="max-sm:w-full">
                <h3 className="font-semibold text-yellow-900 dark:text-yellow-100 max-sm:text-center">
                  Verify Your Email
                </h3>
                <p className="text-yellow-800 dark:text-yellow-200 text-sm max-sm:text-center">
                  Please verify your email address to access all features
                </p>
              </div>
            </div>
            <Link
              to="/verify-email"
              className={`${GlobalStyles.buttonSecondary} !w-full lg:!w-36 max-sm:mt-2`}
            >
              Verify Now
            </Link>
          </div>
        </div>
      )}

      {/* Wallet Connection Section - FIXED */}
      {!isLoadingWallets && !hasConnectedWallets && (
        <div className={`${GlobalStyles.sectionWrap} mb-6`}>
          <div className="text-center py-6">
            <WalletIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className={`${CommonClasses.textGray} mb-2`}>
              No wallet connected
            </p>
            <p
              className={`${CommonClasses.textSm} ${CommonClasses.textGray} mb-4`}
            >
              Connect a wallet to enable ledger balance withdrawals
            </p>
            <button
              onClick={() => setShowWalletModal(true)}
              className={`${GlobalStyles.buttonPrimary} !w-full`}
            >
              Connect Wallet
            </button>
          </div>
        </div>
      )}

      {/* Wallet Loading State */}
      {isLoadingWallets && (
        <div className={`${GlobalStyles.sectionWrap} mb-6`}>
          <div className="animate-pulse">
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/4 mb-4"></div>
            <div className="space-y-3">
              <div className="h-10 bg-gray-300 dark:bg-gray-600 rounded"></div>
              <div className="h-10 bg-gray-300 dark:bg-gray-600 rounded"></div>
            </div>
          </div>
        </div>
      )}

      {/* Balance Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {balanceCards.slice(0, 4).map((card, index) => {
          const Icon = card.icon;
          return (
            <div
              key={index}
              className={`bg-gradient-to-r ${card.gradient} rounded-2xl shadow-lg p-4 text-white transform transition-all duration-300 hover:scale-105 hover:shadow-xl`}
            >
              <div className={`${CommonClasses.flexBetween} mb-3`}>
                <div>
                  <p className="text-blue-100 text-sm font-medium">
                    {card.title}
                  </p>
                  <p className="text-xl font-bold mt-1">
                    ${formatBalanceAmount(card.amount)}
                  </p>
                </div>
                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <Icon className="w-5 h-5 text-white" />
                </div>
              </div>
              <p className="text-blue-100 text-xs opacity-90">
                Available for transactions and transfers
              </p>
            </div>
          );
        })}
      </div>

      {/* Total Balance Card - Full width on mobile */}
      <div className="grid grid-cols-1 gap-4 mb-6">
        {balanceCards.slice(4).map((card, index) => {
          const Icon = card.icon;
          return (
            <div
              key={index}
              className={`bg-gradient-to-r ${card.gradient} rounded-2xl shadow-lg p-6 text-white transform transition-all duration-300 hover:scale-105 hover:shadow-xl`}
            >
              <div className={`${CommonClasses.flexBetween}`}>
                <div>
                  <p className="text-blue-100 text-lg font-medium">
                    {card.title}
                  </p>
                  <p className="text-3xl font-bold mt-2">
                    ${formatBalanceAmount(card.amount)}
                  </p>
                </div>
                <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <Icon className="w-8 h-8 text-white" />
                </div>
              </div>
              <p className="text-blue-100 text-sm opacity-90 mt-2">
                Your complete portfolio value across all balances
              </p>
            </div>
          );
        })}
      </div>

      {/* Crypto Balances Section - UPDATED with Zora and Dogecoin */}
      <div className={`${GlobalStyles.sectionWrap} mb-6`}>
        <div className={`${CommonClasses.flexBetween} mb-4`}>
          <h2
            className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong}`}
          >
            Cryptocurrency Portfolio
          </h2>
          <div className="flex items-center gap-2">
            <CryptoIcon className="w-5 h-5 text-purple-500" />
            <span className="text-sm font-medium text-purple-600">
              Total: ${totalCryptoValue.toFixed(2)}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          {/* Crypto Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3">
            {cryptoCoins.map((coin) => {
              const balance = getCryptoBalance(coin.symbol);
              const usdValue = getCryptoUSDValue(coin.symbol, balance);
              const hasBalance = balance > 0;

              return (
                <div
                  key={coin.symbol}
                  className={`text-center p-3 rounded-xl border transition-all duration-300 ${
                    hasBalance
                      ? "bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 border-purple-200 dark:border-purple-800 hover:shadow-md"
                      : "bg-gray-50 dark:bg-gray-800/30 border-gray-200 dark:border-gray-700"
                  }`}
                >
                  <div className="mb-2">
                    <div className="font-bold text-purple-600 text-sm mb-1">
                      {coin.symbol}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {coin.name}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-base font-semibold text-gray-800 dark:text-gray-200">
                      {balance.toFixed(6)}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">
                      ${usdValue.toFixed(2)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quick Crypto Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
            <Link
              to="/dashboard/deposit"
              className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 hover:shadow-md transition-all duration-300"
            >
              <div className={`${CommonClasses.flexStart} gap-3`}>
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                  <ArrowDownIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">
                    Deposit Crypto
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    Add cryptocurrency to your account
                  </div>
                </div>
              </div>
            </Link>

            <Link
              to="/dashboard/withdraw"
              className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4 hover:shadow-md transition-all duration-300"
            >
              <div className={`${CommonClasses.flexStart} gap-3`}>
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <ArrowUpIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="font-medium text-gray-800 dark:text-gray-200">
                    Withdraw Crypto
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    Send cryptocurrency to external wallet
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Stats and Quick Actions Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Stats Cards */}
        <div className="lg:col-span-1 space-y-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div
                key={index}
                className={`${stat.bgColor} rounded-2xl p-4 border border-gray-200 dark:border-gray-700 transition-all duration-300 hover:shadow-md`}
              >
                <div className={`${CommonClasses.flexBetween}`}>
                  <div>
                    <p
                      className={`${CommonClasses.textSm} font-medium ${CommonClasses.textGray}`}
                    >
                      {stat.title}
                    </p>
                    <p
                      className={`${CommonClasses.textXl} font-bold ${CommonClasses.textGrayStrong} mt-1`}
                    >
                      {stat.value}
                    </p>
                  </div>
                  <Icon className={`w-8 h-8 ${stat.color}`} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="lg:col-span-2">
          <div className={`${GlobalStyles.sectionWrap}`}>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-4`}
            >
              Quick Actions
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <Link
                    key={index}
                    to={action.link}
                    className={`${action.color} text-white rounded-xl p-4 transition-all duration-300 transform hover:scale-105 hover:shadow-lg group`}
                  >
                    <div className={`${CommonClasses.flexStart} gap-3`}>
                      <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center group-hover:bg-white/30 transition-colors duration-200">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">
                          {action.title}
                        </h3>
                        <p className="text-white/80 text-sm mt-1">
                          {action.description}
                        </p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Transactions and Account Status Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Recent Transactions */}
        <div className={`${GlobalStyles.sectionWrap}`}>
          <div className={`${CommonClasses.flexBetween} mb-6`}>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong}`}
            >
              Recent Transactions
            </h2>
            <Link
              to="/dashboard/transactions"
              className={`${CommonClasses.textSm} font-medium text-primary hover:text-primary/80 transition-colors duration-200`}
            >
              View All
            </Link>
          </div>

          {isLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, index) => (
                <div
                  key={index}
                  className="animate-pulse flex items-center space-x-4"
                >
                  <div className="w-10 h-10 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : recentTransactions.length === 0 ? (
            <div className="text-center py-8">
              <HistoryIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className={`${CommonClasses.textGray} text-sm`}>
                No recent transactions
              </p>
              <p
                className={`${CommonClasses.textXs} ${CommonClasses.textGray} mt-1`}
              >
                Your transaction history will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentTransactions.map((transaction) => (
                <div
                  key={transaction.id}
                  className={`${CommonClasses.flexBetween} p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-all duration-200 group`}
                >
                  <div
                    className={`${CommonClasses.flexStart} gap-3 flex-1 min-w-0`}
                  >
                    <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center group-hover:bg-gray-200 dark:group-hover:bg-gray-600 transition-colors duration-200 flex-shrink-0">
                      {getTypeIcon(transaction.type)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`${CommonClasses.flexBetween} gap-2`}>
                        <div className="min-w-0 flex-1">
                          <div
                            className={`font-medium ${getTypeColor(
                              transaction.type,
                            )} truncate`}
                          >
                            {transaction.type.charAt(0).toUpperCase() +
                              transaction.type.slice(1)}
                          </div>
                          <div
                            className={`${CommonClasses.textSm} ${CommonClasses.textGray} truncate`}
                            title={transaction.description || "Transaction"}
                          >
                            {transaction.description || "Transaction"}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div
                            className={`font-semibold ${getAmountColor(
                              transaction,
                            )}`}
                          >
                            {formatAmount(transaction)}{" "}
                            {transaction.coin
                              ? transaction.coin.toUpperCase()
                              : "USD"}
                          </div>
                          <div
                            className={`${CommonClasses.flexStart} gap-2 justify-end mt-1`}
                          >
                            {getBalanceTypeIcon(transaction.balance_type)}
                            <span
                              className={`${
                                CommonClasses.textXs
                              } capitalize ${getBalanceTypeColor(
                                transaction.balance_type,
                              )}`}
                            >
                              {transaction.balance_type}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className={`${CommonClasses.flexBetween} mt-2`}>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                            transaction.status,
                          )}`}
                        >
                          {getStatusIcon(transaction.status)}
                          <span className="ml-1">{transaction.status}</span>
                        </span>
                        <div
                          className={`${CommonClasses.textXs} ${CommonClasses.textGray}`}
                        >
                          {new Date(
                            transaction.created_at,
                          ).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Account Status */}
        <div className={`${GlobalStyles.sectionWrap}`}>
          <h2
            className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong} mb-6`}
          >
            Account Status
          </h2>

          <div className="space-y-6">
            {/* Account Level */}
            <div
              className={`${CommonClasses.flexStart} gap-3 p-4 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl border border-purple-100 dark:border-purple-800`}
            >
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                <VerifiedIcon className="w-6 h-6 text-white" />
              </div>
              <div>
                <div
                  className={`font-semibold ${CommonClasses.textGrayStrong}`}
                >
                  Account Level {user?.account_level}
                </div>
                <div
                  className={`${CommonClasses.textSm} ${CommonClasses.textGray}`}
                >
                  {user?.account_level === "1" && "Email verification required"}
                  {user?.account_level === "2" &&
                    "Email verified - Basic access"}
                  {user?.account_level === "3" && "KYC Verified - Full access"}
                </div>
              </div>
            </div>

            {/* Verification Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                <div
                  className={`${CommonClasses.textLg} font-bold ${
                    user?.email_verified_at
                      ? "text-green-600"
                      : "text-yellow-600"
                  }`}
                >
                  {user?.email_verified_at ? "VERIFIED" : "PENDING"}
                </div>
                <div
                  className={`${CommonClasses.textSm} ${CommonClasses.textGray} mt-1`}
                >
                  Email Verification
                </div>
              </div>

              <div className="text-center p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl border border-gray-200 dark:border-gray-600">
                <div
                  className={`${CommonClasses.textLg} font-bold ${
                    user?.kyc_status === "verified"
                      ? "text-green-600"
                      : user?.kyc_status === "pending"
                        ? "text-yellow-600"
                        : "text-gray-600"
                  }`}
                >
                  {(user?.kyc_status || "NOT STARTED").toUpperCase()}
                </div>
                <div
                  className={`${CommonClasses.textSm} ${CommonClasses.textGray} mt-1`}
                >
                  KYC Verification
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
              <h3 className="font-semibold text-blue-900 dark:text-blue-100 text-sm mb-2">
                Next Steps
              </h3>
              <ul
                className={`${CommonClasses.textSm} text-blue-800 dark:text-blue-200 space-y-1`}
              >
                {!user?.email_verified_at && (
                  <li className={`${CommonClasses.flexStart}`}>
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    Verify your email address
                  </li>
                )}
                {user?.account_level !== "3" && (
                  <li className={`${CommonClasses.flexStart}`}>
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    Complete KYC verification for Level 3
                  </li>
                )}
                <li className={`${CommonClasses.flexStart}`}>
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  Enable two-factor authentication
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Connected Wallets Display - FIXED */}
      {!isLoadingWallets && hasConnectedWallets && (
        <div className={`${GlobalStyles.sectionWrap} mb-6`}>
          <div className={`${CommonClasses.flexBetween} mb-4`}>
            <h2
              className={`${CommonClasses.textLg} font-semibold ${CommonClasses.textGrayStrong}`}
            >
              Wallet Connections
            </h2>
            <button
              onClick={() => setShowWalletModal(true)}
              className={`${CommonClasses.textSm} font-medium text-primary hover:text-primary/80 transition-colors duration-200`}
            >
              Manage
            </button>
          </div>

          <div className="space-y-4">
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className={`${CommonClasses.flexBetween}`}>
                <div className={`${CommonClasses.flexStart} gap-3`}>
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                    <WalletIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-green-800 dark:text-green-200">
                      Wallet Connected
                    </p>
                    <p
                      className={`${CommonClasses.textSm} text-green-700 dark:text-green-300`}
                    >
                      {connectedWallets.length} wallet(s) connected
                    </p>
                  </div>
                </div>
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full dark:bg-green-900 dark:text-green-200">
                  Ready
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {connectedWallets.map((wallet) => (
                <div
                  key={wallet.id}
                  className={`${CommonClasses.flexBetween} p-3 border border-gray-200 dark:border-gray-700 rounded-lg`}
                >
                  <div className={`${CommonClasses.flexStart} gap-3`}>
                    <span className="text-2xl">
                      {wallet.wallet_type === "metamask" && "🦊"}
                      {wallet.wallet_type === "trustwallet" && "🔷"}
                      {wallet.wallet_type === "coinbase" && "🏦"}
                      {wallet.wallet_type === "phantom" && "👻"}
                      {![
                        "metamask",
                        "trustwallet",
                        "coinbase",
                        "phantom",
                      ].includes(wallet.wallet_type) && "💼"}
                    </span>
                    <div>
                      <p
                        className={`font-medium ${CommonClasses.textGrayStrong}`}
                      >
                        {wallet.wallet_name || wallet.wallet_type}
                      </p>
                      <p
                        className={`${CommonClasses.textSm} ${CommonClasses.textGray} capitalize`}
                      >
                        {wallet.wallet_type}
                      </p>
                    </div>
                  </div>
                  <span
                    className={`${CommonClasses.textXs} text-green-600 font-medium`}
                  >
                    Connected
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Display Message from Admin */}
      {user?.display_message && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 dark:from-yellow-900/20 dark:to-orange-900/20 border border-yellow-200 dark:border-yellow-800 rounded-2xl p-6">
          <div className={`${CommonClasses.flexStart} gap-3`}>
            <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-white text-sm">!</span>
            </div>
            <div>
              <h3 className="font-semibold text-yellow-900 dark:text-yellow-100 text-sm">
                Message from Admin
              </h3>
              <p className="text-yellow-800 dark:text-yellow-200 text-sm mt-1">
                {user.display_message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Connection Modal */}
      {showWalletModal && (
        <WalletConnectionModal
          isOpen={showWalletModal}
          onClose={() => setShowWalletModal(false)}
          onConnect={handleWalletConnect}
        />
      )}
    </div>
  );
};

export default DashboardOverview;
